import React from "react";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";

import { DropdownList } from "components";
import { useDropdownQuestionStyles } from "./style";

interface DropdownOption {
  optionId: string;
  htmlText: string;
  sequence: number;
  isSelected: boolean | null;
}

interface DropdownQuestionProps {
  questionId: string;
  questionText: string;
  options: DropdownOption[];
  isMandatory: boolean;
  questionHint?: string | null;
  value: string;
  hasError?: boolean;
  onChange: (questionId: string, optionId: string) => void;
}

export const DropdownQuestion: React.FC<DropdownQuestionProps> = ({
  questionId,
  questionText,
  options,
  isMandatory,
  questionHint,
  value,
  hasError = false,
  onChange,
}) => {
  const classes = useDropdownQuestionStyles();
  const { t } = useTranslation();

  const handleChange = (selectedValue: any) => {
    const optionId = typeof selectedValue === "object" ? selectedValue.optionId : selectedValue;
    onChange(questionId, optionId);
  };

  const selectId = `select-${questionId}`;
  const labelId = `select-label-${questionId}`;

  // Transform options to include both optionId and display text
  const transformedOptions = options.map((option) => ({
    optionId: option.optionId,
    htmlText: option.htmlText,
  }));

  // Find the selected option object
  const selectedOption = transformedOptions.find((opt) => opt.optionId === value) || null;

  return (
    <Box className={classes.container}>
      <DropdownList
        id={selectId}
        label={questionText}
        labelId={labelId}
        labelClassName={classes.label}
        asteriskClassName={classes.requiredAsterisk}
        data={transformedOptions}
        dropdownTextField="htmlText"
        dropdownKey="optionId"
        selectedValue={selectedOption}
        defaultItem={{ optionId: "", htmlText: questionHint || t("select") }}
        required={isMandatory}
        error={hasError && isMandatory}
        onChange={handleChange}
        popupSettings={{ className: classes.selectPopup }}
        className={`${classes.select} ${hasError && isMandatory ? classes.selectError : ""}`}
      />
    </Box>
  );
};
